import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  CheckCircle,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { apiDelete, apiGet, apiPatch } from "./api";
import AdminPageLayout from "./AdminPageLayout";

interface Article {
  id: string;
  title: string;
  titleKo?: string;
  author?: string;
  category?: string;
  status?: string;
  createdBy?: string;
  publishedAt: string;
  createdAt: string;
  isBreaking?: boolean;
}

export default function ArticlesManagement() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createdByFilter, setCreatedByFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const { toast } = useToast();

  useEffect(() => {
    loadArticles();
  }, [statusFilter, createdByFilter]);

  const loadArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (createdByFilter !== "all") {
        params.append("createdBy", createdByFilter);
      }
      params.append("limit", "100"); // Get more articles for filtering

      const data = await apiGet<{ success: boolean; data: Article[] }>(
        `/api/admin/articles?${params.toString()}`
      );

      const articlesData = data.success
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setArticles(articlesData);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load articles";
      setError(errorMessage);
      console.error("[ArticlesManagement] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await apiPatch(`/api/articles/${id}`, { status });
      toast({
        title: "상태 변경 완료",
        description: `기사 상태가 '${status}'(으)로 변경되었습니다.`,
      });
      loadArticles();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update status";
      toast({
        title: "오류",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("이 기사를 복구 가능한 보관 상태로 이동하시겠습니까?")) return;

    try {
      await apiDelete(`/api/articles/${id}`);
      toast({
        title: "보관 완료",
        description: "기사가 공개 목록에서 제외되었으며 필요하면 복구할 수 있습니다.",
      });
      loadArticles();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete article";
      toast({
        title: "오류",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      searchTerm === "" ||
      (article.titleKo || article.title || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <AdminPageLayout
      title="Articles Management"
      description="Manage and monitor articles, including bot-generated ones"
      icon={<FileText className="w-8 h-8" />}
      loading={loading}
      error={error}
    >
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>기사 관리</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                직접 작성, AI 편집, 이미지 업로드·생성, 검토와 발행을 관리합니다.
              </p>
            </div>
            <Link href="/admin/new">
              <Button data-testid="button-new-article">
                <Plus className="mr-2 h-4 w-4" /> 새 기사 작성
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={createdByFilter}
              onValueChange={(value) => {
                setCreatedByFilter(value);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Created By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="bot-local">Bot (Local)</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Articles Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">Title</th>
                  <th className="text-left p-2 font-medium">Author</th>
                  <th className="text-left p-2 font-medium">Category</th>
                  <th className="text-left p-2 font-medium">Status</th>
                  <th className="text-left p-2 font-medium">Created By</th>
                  <th className="text-left p-2 font-medium">Created</th>
                  <th className="text-left p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedArticles.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center p-8 text-muted-foreground"
                    >
                      No articles found
                    </td>
                  </tr>
                ) : (
                  paginatedArticles.map((article) => (
                    <tr key={article.id} className="border-b hover:bg-muted/50">
                      <td className="p-2">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {article.titleKo || article.title}
                          </span>
                          {article.isBreaking && (
                            <Badge
                              variant="destructive"
                              className="w-fit mt-1 text-xs"
                            >
                              Breaking
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-muted-foreground">
                        {article.author || "OZK News"}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline">
                          {article.category || "-"}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <Badge
                          variant={
                            article.status === "published"
                              ? "default"
                              : article.status === "pending"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {article.status || "published"}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {article.createdBy || "unknown"}
                        </code>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {format(new Date(article.createdAt), "PPp", {
                          locale: ko,
                        })}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {article.status === "pending" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleStatusUpdate(article.id, "published")
                                }
                                className="h-8"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleStatusUpdate(article.id, "draft")
                                }
                                className="h-8"
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Link href={`/admin/edit/${article.id}`}>
                            <Button variant="outline" size="sm" className="h-8">
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(article.id)}
                            className="h-8"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({filteredArticles.length}{" "}
                articles)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
